import { useState, useMemo, useCallback } from 'react';
import { createListCollection } from '@chakra-ui/react';
import { useTranslation } from '../hooks/useTranslation';

export type PaymentPeriod = {
    value: string;
    months: number;
    label: string;
};

type UsePaymentPeriodsParams = {
    onPeriodChange?: (period: PaymentPeriod) => void;
    defaultPeriodIndex?: number; // Индекс периода по умолчанию (по умолчанию 1 - 3 месяца)
    minMonths?: number;
};

export const usePaymentPeriods = (params: UsePaymentPeriodsParams) => {
    const { t } = useTranslation();
    const { onPeriodChange, defaultPeriodIndex = 1, minMonths = 0 } = params;

    // Периоды оплаты
    const periods: PaymentPeriod[] = useMemo(
        () =>
            [
                { value: '1', months: 1, label: t.pages.payment.payFor1Month },
                { value: '3', months: 3, label: t.pages.payment.payFor3Months },
                { value: '6', months: 6, label: t.pages.payment.payFor6Months },
                { value: '12', months: 12, label: t.pages.payment.payFor12Months },
                { value: 'custom', months: 0, label: t.pages.payment.customAmount },
            ].filter((p) => p.months >= minMonths || p.value === 'custom'),
        [minMonths],
    );

    const customPeriod: PaymentPeriod = periods.find((p) => p.value === 'custom') as PaymentPeriod;
    const [selectedValue, setSelectedValue] = useState<string | null>(null);

    // Период вычисляем, а не храним: список фильтруется по minMonths, который известен только
    // после загрузки клиента. Если хранить период в state, выбранным может остаться период,
    // которого уже нет в списке
    const selectedPeriod: PaymentPeriod = useMemo(() => {
        const period = periods.find((p) => p.value === selectedValue);
        if (period) return period;
        return periods[Math.min(defaultPeriodIndex, periods.length - 1)];
    }, [periods, selectedValue, defaultPeriodIndex]);

    // Обработчик изменения периода
    const handlePeriodChange = (value: string) => {
        const period = periods.find((p) => p.value === value) as PaymentPeriod;
        setSelectedValue(value);
        onPeriodChange?.(period);
    };

    // Функция для установки кастомного периода
    const setCustomPeriod = useCallback(() => {
        setSelectedValue(customPeriod.value);
    }, [customPeriod.value]);

    // Создаем коллекцию для селекта
    const periodCollection = useMemo(() => {
        return createListCollection({ items: periods });
    }, [periods]);

    return {
        selectedPeriod,
        periodCollection,
        handlePeriodChange,
        setCustomPeriod,
    };
};
